import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Image } from 'expo-image';
import Svg, { Rect } from 'react-native-svg';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import Text from '@/components/Typography/Text';
import { useLanding } from './LandingKit';
import { MONO, fluid, isWeb, prefersReducedMotion } from './landingTokens';

const MOUNTAIN = require('@/assets/images/landing-mountain.webp');
const COAST = require('@/assets/images/landing-beach.webp');
const EXAMPLES = [
  { id: 'website', label: 'A website', icon: 'globe', goal: 'Build a beautiful website for my mountain retreat.', result: 'Your next adventure, brought to life.', file: 'mountain-retreat.html', format: 'Website · HTML', steps: ['Shape the story and layout', 'Build the responsive website', 'Package the source files'] },
  { id: 'presentation', label: 'A presentation', icon: 'file-powerpoint', goal: 'Turn my product launch notes into a presentation for the team.', result: 'A big idea. Ready for the room.', file: 'launch-plan.pptx', format: 'Presentation · PowerPoint', steps: ['Organize the key ideas', 'Design the presentation', 'Prepare an editable deck'] },
  { id: 'document', label: 'A document', icon: 'file-lines', goal: 'Turn my research notes into a polished report I can share as a Word document or PDF.', result: 'Your thinking. Beautifully put together.', file: 'research-brief.docx', format: 'Document · Word & PDF', steps: ['Organize the research and sources', 'Write and format the report', 'Prepare Word and PDF versions'] },
  { id: 'spreadsheet', label: 'A spreadsheet', icon: 'table', goal: 'Create a monthly operating budget for my small business.', result: 'Every detail, a little clearer.', file: 'monthly-budget.xlsx', format: 'Spreadsheet · Excel', steps: ['Organize the expenses', 'Build the budget and totals', 'Prepare an editable workbook'] },
  { id: 'video', label: 'A video', icon: 'film', goal: 'Create a short film about a summer by the sea, with music and a voiceover.', result: 'Your story. Ready for the screen.', file: 'golden-hour.mp4', format: 'Video · MP4', steps: ['Write the script and shot list', 'Create the scenes and voiceover', 'Bring it together with music'] },
  { id: 'code', label: 'A coding project', icon: 'code', goal: 'Write a Python function that removes duplicate CSV rows, with tests.', result: 'From a rough idea to working code.', file: 'csv-cleaner.zip', format: 'Python · Source & tests', steps: ['Write the cleanup function', 'Test duplicates and edge cases', 'Deliver the source and tests'] },
  { id: 'game', label: 'A video game', icon: 'gamepad', goal: 'Build a space arcade game with movement, scoring, and a restart button.', result: 'An idea you can play.', file: 'orbit-run.html', format: 'Video game · HTML', steps: ['Design the game and artwork', 'Build the controls and scoring', 'Package the playable game'] },
];

function WebsitePreview({ compact }) {
  return (
    <View style={{ flex: 1, minHeight: 260, backgroundColor: '#17392B', overflow: 'hidden', borderRadius: 6 }}>
      <Image source={MOUNTAIN} contentFit="cover" accessibilityLabel="Sunrise over a mountain valley" style={StyleSheet.absoluteFillObject} />
      <View style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(7,24,18,.30)' }]} />
      <View style={{ flex: 1, justifyContent: 'space-between', padding: compact ? 22 : 30 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text weight="bold" style={{ color: '#FFFFFF', fontSize: 11, letterSpacing: 2 }}>EVERGREEN</Text>
          <Text style={{ color: '#FFFFFF', fontSize: 10 }}>A little closer to nature.</Text>
        </View>
        <View style={{ gap: 12, marginTop: 44 }}>
          <Text weight="semibold" style={{ color: '#FFFFFF', fontSize: compact ? 34 : 46, lineHeight: compact ? 37 : 49, letterSpacing: -1.5 }}>{'Find your\nnext adventure.'}</Text>
          <Text style={{ color: '#FFFFFF', fontSize: 12, lineHeight: 18 }}>Slow mornings. Open skies. A place to reconnect.</Text>
        </View>
        <View style={{ marginTop: 28, paddingTop: 14, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,.4)', flexDirection: 'row', gap: 24 }}>
          {['Stay a little longer', 'Explore the outdoors'].map(label => <Text key={label} style={{ color: '#FFFFFF', fontSize: 10 }}>{label}</Text>)}
        </View>
      </View>
    </View>
  );
}

function PresentationPreview({ compact }) {
  return (
    <View style={{ flex: 1, minHeight: 300, borderRadius: 6, overflow: 'hidden', backgroundColor: '#F5F0EC', flexDirection: 'row' }}>
      <View style={{ flex: 1.2, padding: compact ? 20 : 30, justifyContent: 'space-between' }}>
        <Text weight="bold" style={{ color: '#735849', fontSize: 10, letterSpacing: 1.4 }}>NORTHSTAR / LAUNCH PLAN</Text>
        <View style={{ gap: 14, marginVertical: 32 }}>
          <Text weight="semibold" style={{ color: '#382C28', fontSize: compact ? 28 : 38, lineHeight: compact ? 32 : 41, letterSpacing: -1.1 }}>{'Good ideas.\nGreat beginnings.'}</Text>
          <Text style={{ color: '#6B584D', fontSize: 12, lineHeight: 19 }}>One clear story. A shared direction. A plan to bring it to life.</Text>
        </View>
        <Text style={{ color: '#6B584D', fontSize: 10 }}>The next chapter. / 01</Text>
      </View>
      <View style={{ flex: 0.8, padding: compact ? 12 : 24, justifyContent: 'center', gap: 14, backgroundColor: '#EBDDD2' }}>
        {[['01', 'Shape', '#FAF6F0'], ['02', 'Launch', '#C48465'], ['03', 'Grow', '#493932']].map(([number, label, color], index) => (
          <View key={number} style={{ backgroundColor: color, borderRadius: 6, padding: compact ? 12 : 18, gap: 10 }}>
            <Text style={{ color: index ? '#FFFFFF' : '#735849', fontSize: 11 }}>{number}</Text>
            <Text weight="semibold" style={{ color: index ? '#FFFFFF' : '#382C28', fontSize: compact ? 17 : 23, letterSpacing: -0.5 }}>{label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function SpreadsheetPreview({ compact }) {
  const rows = [['Rent', '$1,200', 'Workspace'], ['Software', '$180', 'Subscriptions'], ['Marketing', '$450', 'Campaigns'], ['Other', '$170', 'Supplies']];
  return (
    <View style={{ flex: 1, minHeight: 260, padding: compact ? 20 : 28, backgroundColor: '#FFFFFF', borderRadius: 6 }}>
      <Text weight="semibold" style={{ color: '#17392B', fontSize: 23, letterSpacing: -0.7 }}>A clearer month ahead.</Text>
      <Text style={{ color: '#647067', fontSize: 11, marginTop: 7, marginBottom: 20 }}>Your business budget. Everything in view.</Text>
      <View accessibilityRole={isWeb ? 'table' : undefined} accessibilityLabel="Example monthly budget">
        {[[ 'Expense', 'Budget', 'Details' ], ...rows, ['Total', '$2,000', 'Per month']].map((row, index) => (
          <View key={row[0]} accessibilityRole={isWeb ? 'row' : undefined} style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 9, backgroundColor: index === 0 || index === 5 ? '#F0F3EE' : '#FFFFFF', borderBottomWidth: index < 5 ? 1 : 0, borderBottomColor: '#E9ECE7' }}>
            {row.map((cell, column) => <Text key={cell} accessibilityRole={isWeb ? (index === 0 ? 'columnheader' : 'cell') : undefined} weight={index === 0 || index === 5 ? 'semibold' : 'normal'} style={{ flex: column === 0 ? 1.15 : 1, color: '#324537', fontSize: 11, textAlign: column === 1 ? 'right' : 'left', paddingLeft: column === 2 ? 16 : 0 }}>{cell}</Text>)}
          </View>
        ))}
      </View>
    </View>
  );
}

function VideoPreview({ compact }) {
  return (
    <View style={{ flex: 1, minHeight: 300, borderRadius: 6, overflow: 'hidden', backgroundColor: '#13241D' }}>
      <Image source={COAST} contentFit="cover" accessibilityLabel="A tropical coast at sunset in an illustrative AI film" style={StyleSheet.absoluteFillObject} />
      <View style={[StyleSheet.absoluteFillObject, { backgroundColor: 'rgba(6,19,16,.46)' }]} />
      <View style={{ flex: 1, padding: compact ? 22 : 30, justifyContent: 'space-between' }}>
        <View style={styles.row}><Icon name="film" size={12} color="#FFFFFF" /><Text style={{ color: '#FFFFFF', fontSize: 10, letterSpacing: 1.8 }}>GOLDEN HOUR / A SHORT FILM</Text></View>
        <View style={{ gap: 12, marginVertical: 32 }}>
          <Text weight="semibold" style={{ color: '#FFFFFF', fontSize: compact ? 33 : 44, lineHeight: compact ? 37 : 48, letterSpacing: -1.3 }}>{'Chase the\nlast light.'}</Text>
          <Text style={{ color: '#FFFFFF', fontSize: 12, lineHeight: 19 }}>AI scenes. An original soundtrack. Your story.</Text>
        </View>
        <View style={{ gap: 11 }}>
          <View style={{ height: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,.35)' }}><View style={{ height: 3, width: '40%', borderRadius: 2, backgroundColor: '#FFFFFF' }} /></View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}><Text style={{ color: '#FFFFFF', fontSize: 10 }}>00:12 / 00:30</Text><Text style={{ color: '#FFFFFF', fontSize: 10 }}>Scene 02 / The shoreline</Text></View>
        </View>
      </View>
    </View>
  );
}

function GamePreview({ compact }) {
  return (
    <View style={{ flex: 1, minHeight: 300, borderRadius: 6, overflow: 'hidden', backgroundColor: '#10182B', padding: compact ? 22 : 30, justifyContent: 'space-between' }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12 }}>
        <Text weight="bold" style={{ color: '#D5F3E8', fontSize: 11, letterSpacing: 1.6 }}>ORBIT RUN</Text>
        <Text style={{ color: '#A8BAD0', fontSize: 10 }}>SCORE 001250</Text>
      </View>
      <View accessibilityRole="image" accessibilityLabel="Illustrative space arcade game with a pixel spaceship, stars, and asteroids" style={{ height: compact ? 170 : 205, marginVertical: 10 }}>
        <Svg width="100%" height="100%" viewBox="0 0 480 200" aria-hidden={isWeb ? true : undefined}>
          {Array.from({ length: 30 }, (_, i) => <Rect key={`star-${i}`} x={(i * 137 + 13) % 478} y={(i * 73 + 11) % 198} width={i % 4 ? 2 : 3} height={i % 4 ? 2 : 3} fill={i % 3 ? '#718AA9' : '#D5F3E8'} />)}
          {[70, 185, 320, 405].map((x, i) => <React.Fragment key={x}><Rect x={x} y={22 + i % 2 * 42} width={24} height={24} fill="#B29AD7" /><Rect x={x - 6} y={28 + i % 2 * 42} width={36} height={12} fill="#B29AD7" /><Rect x={x + 6} y={28 + i % 2 * 42} width={6} height={6} fill="#74648F" /></React.Fragment>)}
          <Rect x="237" y="106" width="6" height="14" fill="#C8EAA1" />
          <Rect x="237" y="132" width="6" height="30" fill="#E8F5E7" />
          <Rect x="231" y="144" width="18" height="24" fill="#E8F5E7" />
          <Rect x="219" y="156" width="42" height="12" fill="#9DD8C4" />
          <Rect x="237" y="150" width="6" height="12" fill="#4A8395" />
          <Rect x="231" y="174" width="6" height="12" fill="#EDB978" />
          <Rect x="243" y="174" width="6" height="12" fill="#EDB978" />
        </Svg>
      </View>
      <View style={{ borderTopWidth: 1, borderTopColor: 'rgba(168,186,208,.22)', paddingTop: 14, gap: 6 }}>
        <Text weight="semibold" style={{ color: '#E6EDF6', fontSize: 15 }}>A small game. A whole new world.</Text>
        <Text style={{ color: '#A8BAD0', fontSize: 11, lineHeight: 17 }}>Movement, scoring, and one more try.</Text>
      </View>
    </View>
  );
}

function DocumentPreview({ compact }) {
  return (
    <View style={{ flex: 1, minHeight: 300, padding: compact ? 12 : 20, borderRadius: 6, backgroundColor: '#EDEBF0', alignItems: 'center' }}>
      <View style={{ flex: 1, width: '100%', maxWidth: 460, padding: compact ? 20 : 26, backgroundColor: '#FFFFFF', borderRadius: 3, gap: 16, ...(isWeb ? { boxShadow: '0 3px 12px rgba(44,37,59,.08)' } : {}) }}>
        <View style={{ ...styles.row, justifyContent: 'space-between' }}>
          <Text weight="semibold" style={{ color: '#706078', fontSize: 9, letterSpacing: 1.5 }}>RESEARCH BRIEF</Text>
          <Text style={{ color: '#706078', fontSize: 9 }}>WORD + PDF</Text>
        </View>
        <Text weight="semibold" style={{ color: '#302838', fontSize: compact ? 25 : 30, lineHeight: compact ? 29 : 34, letterSpacing: -0.9 }}>A better workweek.</Text>
        <View style={{ height: 2, width: 36, backgroundColor: '#9A86AF' }} />
        <Text style={{ color: '#655E6B', fontSize: 12, lineHeight: 19 }}>A practical plan for fewer meetings, clearer priorities, and more time for focused work.</Text>
        <View style={{ gap: 12 }}>
          {['Protect focused time', 'Make priorities visible', 'Review what’s working'].map((title, index) => (
            <View key={title} style={styles.row}>
              <Text style={{ color: '#9A86AF', fontSize: 10 }}>0{index + 1}</Text>
              <Text weight="medium" style={{ color: '#44374C', fontSize: 12, lineHeight: 18, flex: 1 }}>{title}</Text>
            </View>
          ))}
        </View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingTop: 14, borderTopWidth: 1, borderTopColor: '#EDE8F0', marginTop: 'auto' }}>
          <Text style={{ color: '#706078', fontSize: 9 }}>Research & recommendations</Text>
          <Text style={{ color: '#706078', fontSize: 9 }}>01</Text>
        </View>
      </View>
    </View>
  );
}

function CodePreview({ compact }) {
  const lines = [
    ['def clean_rows(rows):', '#C5ABF4'],
    ['    seen = set()', '#D6E0EE'],
    ['    result = []', '#D6E0EE'],
    ['    for row in rows:', '#C5ABF4'],
    ['        key = tuple(row)', '#D6E0EE'],
    ['        if key not in seen:', '#C5ABF4'],
    ['            seen.add(key)', '#9DD8C4'],
    ['            result.append(row)', '#9DD8C4'],
    ['    return result', '#C5ABF4'],
  ];
  return (
    <View style={{ flex: 1, minHeight: 300, borderRadius: 6, overflow: 'hidden', backgroundColor: '#111A24' }}>
      <View style={{ ...styles.row, paddingHorizontal: 18, paddingVertical: 15, borderBottomWidth: 1, borderBottomColor: '#2D3847' }}>
        <Icon name="code" size={12} color="#9DD8C4" />
        <Text weight="medium" style={{ color: '#E1E9F3', fontSize: 12, flex: 1 }}>clean_rows.py</Text>
        <Text style={{ color: '#9CAFC3', fontSize: 10 }}>Python</Text>
      </View>
      <View accessibilityLabel="Example Python source code" style={{ paddingVertical: 18, paddingHorizontal: compact ? 12 : 22, flex: 1 }}>
        {lines.map(([line, color], index) => (
          <View key={line} style={{ flexDirection: 'row', gap: compact ? 10 : 18 }}>
            <Text aria-hidden={isWeb ? true : undefined} style={{ color: '#77899D', fontFamily: MONO, fontSize: compact ? 10 : 12, lineHeight: 20, width: 12, textAlign: 'right' }}>{index + 1}</Text>
            <Text tag="code" style={{ color, fontFamily: MONO, fontSize: compact ? 10 : 12, lineHeight: 20, ...(isWeb ? { whiteSpace: 'pre' } : {}) }}>{line}</Text>
          </View>
        ))}
      </View>
      <View style={{ paddingHorizontal: 18, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#2D3847', gap: 7 }}>
        <View style={styles.row}><Icon name="check" size={11} color="#9DD8C4" /><Text weight="medium" style={{ color: '#9DD8C4', fontSize: 12 }}>3 tests passed</Text></View>
        <Text style={{ color: '#9CAFC3', fontSize: 10, lineHeight: 16 }}>Empty input · Duplicate rows · Original order</Text>
      </View>
    </View>
  );
}

/** Selectable, explicitly illustrative outcomes. No simulated inputs or downloads. */
function HeroProductPreview() {
  const { c, width } = useLanding();
  const compact = width < 850;
  const narrow = width < 430;
  const [selected, setSelected] = useState('website');
  const [playing, setPlaying] = useState(() => !prefersReducedMotion());
  const [inView, setInView] = useState(!isWeb);
  const [pageVisible, setPageVisible] = useState(true);
  const previewRef = useRef(null);

  useEffect(() => {
    if (!isWeb) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting && entry.intersectionRatio >= 0.15), { threshold: 0.15 });
    if (previewRef.current) observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isWeb) return undefined;
    const onVisibility = () => setPageVisible(document.visibilityState !== 'hidden');
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const onMotion = (event) => { if (event.matches) setPlaying(false); };
    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);
    motion?.addEventListener?.('change', onMotion);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      motion?.removeEventListener?.('change', onMotion);
    };
  }, []);

  useEffect(() => {
    if (!playing || !inView || !pageVisible) return undefined;
    const timer = setInterval(() => {
      setSelected(current => EXAMPLES[(EXAMPLES.findIndex(item => item.id === current) + 1) % EXAMPLES.length].id);
    }, 8000);
    return () => clearInterval(timer);
  }, [playing, inView, pageVisible]);

  const example = EXAMPLES.find(item => item.id === selected);

  return (
    <View ref={previewRef} style={{ gap: 18 }}>
      <View accessibilityRole={isWeb ? 'group' : undefined} accessibilityLabel="Explore what you can create" style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignSelf: 'center', maxWidth: '100%', padding: 4, gap: 2, borderRadius: width < 1000 ? 22 : 999, backgroundColor: c.panel, borderWidth: 1, borderColor: c.rule }}>
        {EXAMPLES.map(item => (
          <Pressable key={item.id} accessibilityRole="button" accessibilityLabel={`Preview ${item.label.toLowerCase()}`} accessibilityState={{ selected: item.id === selected }} {...(isWeb ? { 'aria-pressed': item.id === selected, 'aria-controls': 'landing-product-example', dataSet: { landingPreviewChoice: 'true' } } : {})} onFocus={() => setPlaying(false)} onPress={() => { setSelected(item.id); setPlaying(false); }} style={{ minHeight: 44, paddingHorizontal: narrow ? 12 : 18, borderRadius: 999, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: item.id === selected ? c.cardSolid : 'transparent', ...(isWeb ? { cursor: 'pointer', boxShadow: item.id === selected ? c.shadowSoft : 'none' } : {}) }}>
            {!narrow && <Icon name={item.icon} size={13} color={item.id === selected ? c.ink : c.muted} />}
            <Text weight={item.id === selected ? 'semibold' : 'medium'} style={{ fontSize: narrow ? 11 : 13, color: item.id === selected ? c.ink : c.inkSoft }}>{item.label}</Text>
          </Pressable>
        ))}
      </View>
      <View nativeID="landing-product-example" accessibilityRole={isWeb ? 'region' : undefined} accessibilityLabel={`${example.label} example`} style={{ borderWidth: 1, borderColor: c.ruleStrong, borderRadius: compact ? 20 : 24, overflow: 'hidden', backgroundColor: c.cardSolid, ...(isWeb ? { boxShadow: c.isDark ? '0 24px 70px -30px rgba(0,0,0,.65)' : '0 24px 70px -30px rgba(24,43,31,.22)' } : {}) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 14, paddingHorizontal: compact ? 20 : 26, height: 54, borderBottomWidth: 1, borderBottomColor: c.rule }}>
          <View style={styles.row}><Logo width={20} height={20} color={c.ink} /><Text weight="semibold" style={{ color: c.ink, fontSize: 13 }}>Gab AI</Text><Text style={{ color: c.muted, fontSize: 12 }}> / Work</Text></View>
          <Text style={{ color: c.muted, fontSize: 11 }}>Illustrative example</Text>
        </View>
        <View style={{ flexDirection: compact ? 'column' : 'row' }}>
          <View style={{ width: compact ? '100%' : '37%', padding: compact ? 20 : 32, gap: compact ? 16 : 28, justifyContent: 'space-between' }}>
            <View style={{ gap: 14 }}>
              {!compact && <Text weight="medium" style={{ color: c.muted, fontSize: 12 }}>You bring the idea.</Text>}
              <View style={{ padding: compact ? 0 : 18, borderRadius: 16, backgroundColor: compact ? 'transparent' : c.panel }}><Text style={{ color: c.ink, fontSize: compact ? 14 : 16, lineHeight: compact ? 22 : 25 }}>{example.goal}</Text></View>
            </View>
            {!compact && <View style={{ gap: 17 }}>{example.steps.map(step => <View key={step} style={styles.row}><Icon name="check" size={11} color={c.accent} /><Text style={{ color: c.inkSoft, fontSize: 13, flex: 1, lineHeight: 20 }}>{step}</Text></View>)}</View>}
            <View style={styles.row}><View style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: c.accentWash, alignItems: 'center', justifyContent: 'center' }}><Icon name="check" size={10} color={c.accent} /></View><Text weight="medium" style={{ color: c.accent, fontSize: 12 }}>Ready for your finishing touch.</Text></View>
          </View>
          <View style={{ flex: 1, minWidth: 0, padding: compact ? 12 : 20, backgroundColor: c.panel, borderLeftWidth: compact ? 0 : 1, borderTopWidth: compact ? 1 : 0, borderColor: c.rule, gap: 12 }}>
            <View style={{ paddingHorizontal: 3, paddingBottom: 2 }}><Text weight="medium" style={{ color: c.inkSoft, fontSize: 12 }}>{example.result}</Text></View>
            <View style={{ minHeight: compact ? 300 : 342 }}>
              {selected === 'website' && <WebsitePreview compact={compact} />}
              {selected === 'presentation' && <PresentationPreview compact={compact} />}
              {selected === 'spreadsheet' && <SpreadsheetPreview compact={compact} />}
              {selected === 'document' && <DocumentPreview compact={compact} />}
              {selected === 'video' && <VideoPreview compact={compact} />}
              {selected === 'game' && <GamePreview compact={compact} />}
              {selected === 'code' && <CodePreview compact={compact} />}
            </View>
            <View style={{ ...styles.row, paddingHorizontal: 3, paddingVertical: 3 }}><Icon name={example.icon} size={13} color={c.accent} /><Text weight="medium" style={{ color: c.ink, fontSize: 12, flex: 1 }}>{example.file}</Text>{!narrow && <Text style={{ color: c.muted, fontSize: 11 }}>{example.format}</Text>}</View>
          </View>
        </View>
      </View>
      <Text style={{ color: c.muted, fontSize: fluid(width, 11, 12), lineHeight: 18, textAlign: 'center' }}>Your idea. A little direction. Something you can actually use.</Text>
    </View>
  );
}

const styles = StyleSheet.create({ row: { flexDirection: 'row', alignItems: 'center', gap: 9 } });
export default React.memo(HeroProductPreview);
